import _ from 'lodash';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';

import { I18n } from 'common/i18n';

import { actionComponentCancel } from 'store/reducers/ActionComponentReducer';
import { chosenMoveComponentDestination } from 'store/reducers/StoryReducer';
import { selectors } from 'store/selectors/ActionComponentSelectors';
import type { StorytellerState } from 'store/StorytellerReduxState';
import { COMPONENT_ACTION_TYPES } from 'lib/Constants';

/**
 * When activated, grays out all components except the selected one,
 * and shows action buttons on all other relevant components.
 */
export const ComponentActionOverlay = ({
  blockId,
  componentIndex,
}: {
  blockId?: string;
  componentIndex?: number;
}) => {
  const dispatch = useDispatch();

  // Use useSelector hooks for all state access
  const isUserChoosingMoveDestination = useSelector(selectors.isUserChoosingMoveDestination);

  const isComponentBeingMoved = useSelector((state: StorytellerState) =>
    blockId && componentIndex != null
      ? selectors.isComponentBeingMoved(blockId, componentIndex, state)
      : false,
  );

  const isComponentValidMoveDestination = useSelector((state: StorytellerState) =>
    blockId && componentIndex != null
      ? selectors.isComponentValidMoveDestination(blockId, componentIndex, state)
      : false,
  );

  // Get source info with hooks
  const sourceBlockId = useSelector(selectors.getSourceBlockId);
  const sourceComponentIndex = useSelector(selectors.getSourceComponentIndex);

  const isActive = isUserChoosingMoveDestination;
  const shouldShowActionButton = isComponentValidMoveDestination;
  const shouldShowCancelButton = isComponentBeingMoved;
  const actionButtonText = I18n.t('editor.components.edit_controls.swap_here');

  if (!isActive) {
    return null;
  }

  const overlayClasses = classnames('component-edit-action-overlay', {
    'action-target': shouldShowActionButton,
    'action-source': shouldShowCancelButton,
  });

  const onActionClick = (): void => {
    const sourceComponent = {
      blockId: sourceBlockId,
      componentIndex: sourceComponentIndex,
    };

    dispatch(
      chosenMoveComponentDestination({
        blockId,
        componentIndex,
        sourceComponent,
      }),
    );
  };

  const onCancelAction = (): void => {
    dispatch(actionComponentCancel());
  };

  const overlayProps = {
    className: overlayClasses,
    onClick: shouldShowActionButton ? onActionClick : _.noop,
  };

  return (
    <div className={`component-edit-${COMPONENT_ACTION_TYPES.MOVE}-overlay-container`}>
      <div {...overlayProps}>
        {shouldShowActionButton && (
          <button className="btn btn-alternate-2 btn-action-target">{actionButtonText}</button>
        )}
        {shouldShowCancelButton && (
          <button className="btn btn-simple btn-action-cancel" onClick={onCancelAction || _.noop}>
            {I18n.t('editor.components.edit_controls.cancel')}
          </button>
        )}
      </div>
    </div>
  );
};
